export type VoiceTranscript = {
  role: "user" | "assistant";
  text: string;
  timestamp: Date;
};

export type ParsedVoiceEvent =
  | { kind: "session_ready" }
  | { kind: "audio_delta"; pcm: Uint8Array }
  | { kind: "user_transcript"; text: string }
  | { kind: "assistant_delta"; text: string }
  | { kind: "error"; reason?: string; error?: string }
  | { kind: "ignore" };

export function decodeBase64Bytes(delta: string): Uint8Array {
  const binaryString = atob(delta);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

function errorMessage(value: unknown): string | undefined {
  if (typeof value === "string" && value) return value;
  if (value && typeof value === "object") {
    const row = value as Record<string, unknown>;
    if (typeof row.message === "string" && row.message) return row.message;
    if (typeof row.error === "string" && row.error) return row.error;
  }
  return undefined;
}

export function parseVoiceEvent(data: unknown): ParsedVoiceEvent {
  if (!data || typeof data !== "object") return { kind: "ignore" };

  const event = data as Record<string, unknown>;
  const type = typeof event.type === "string" ? event.type : "";

  switch (type) {
    case "session.ready":
    case "session.started":
      return { kind: "session_ready" };

    case "session.output_audio.delta":
      if (typeof event.delta !== "string" || event.delta.length === 0) {
        return { kind: "ignore" };
      }
      return { kind: "audio_delta", pcm: decodeBase64Bytes(event.delta) };

    case "session.input_transcript.delta":
      if (typeof event.delta !== "string" || !event.delta) {
        return { kind: "ignore" };
      }
      return { kind: "user_transcript", text: event.delta };

    case "session.output_transcript.delta":
      if (typeof event.delta !== "string" || !event.delta) {
        return { kind: "ignore" };
      }
      return { kind: "assistant_delta", text: event.delta };

    case "error":
      return {
        kind: "error",
        reason: typeof event.reason === "string" ? event.reason : undefined,
        error: errorMessage(event.error) ?? errorMessage(event.message),
      };

    default:
      return { kind: "ignore" };
  }
}

export function applySpeakerDelta(
  prev: VoiceTranscript[],
  role: VoiceTranscript["role"],
  delta: string,
  now: Date = new Date(),
): VoiceTranscript[] {
  const last = prev[prev.length - 1];
  if (last?.role === role) {
    return [
      ...prev.slice(0, -1),
      { ...last, text: last.text + delta },
    ];
  }
  return [...prev, { role, text: delta, timestamp: now }];
}

export function applyAssistantDelta(
  prev: VoiceTranscript[],
  delta: string,
  now: Date = new Date(),
): VoiceTranscript[] {
  return applySpeakerDelta(prev, "assistant", delta, now);
}

export function applyUserDelta(
  prev: VoiceTranscript[],
  delta: string,
  now: Date = new Date(),
): VoiceTranscript[] {
  return applySpeakerDelta(prev, "user", delta, now);
}

export function markAssistantFinal(prev: VoiceTranscript[]): VoiceTranscript[] {
  const last = prev[prev.length - 1];
  if (last?.role === "assistant") {
    return [
      ...prev.slice(0, -1),
      { ...last, text: last.text + "[final]" },
    ];
  }
  return prev;
}
